/* eslint-disable react-hooks/exhaustive-deps */
// --------------------------------------------------
//  GlobalSocketListener
//  ・チャットページ外では簡易トーストを出す（未読数は MessageCountContext 側で管理）
// --------------------------------------------------
import { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useArtistAuth } from '../../contexts/ArtistAuthContext';
import { ADMIN_MESSAGE_FORMATS } from '../../constants/messageFormats';
import { resolveAdminPermission } from '../../constants/adminPermissionCatalog';
import { getEventDetail } from '../../api/taskEvents';
import { getSocket } from '../../lib/socket';
import { dispatchAdminContractBadgeRefresh, SOCKET_EVENTS } from '../../lib/socketEvents';
import {
  getActiveChatContext,
  isMatchingActiveChatContext,
} from '../../utils/activeChatContext';

const NORMAL_CHAT_FORMATS = new Set([
  ADMIN_MESSAGE_FORMATS.TEXT,
  ADMIN_MESSAGE_FORMATS.IMAGE,
  ADMIN_MESSAGE_FORMATS.FILE,
]);

const isSystemMessageFormat = (messageFormat = '') =>
  !!messageFormat && !NORMAL_CHAT_FORMATS.has(messageFormat);

  const resolveMessageFormat = (payload = {}) =>
  payload.messageFormat || payload.message_format || '';

const resolveSenderArtistId = (payload = {}) =>
  payload.senderArtistId || payload.sender_artist_id || null;

const GlobalSocketListener = () => {
  // ── 認証情報（どちらかだけ入る）
  const { user }        = useAuth();        // admin
  const { artistUser }  = useArtistAuth();  // artist
  const isTaskOnlyAdmin =
    user?.userType === 'admin' && String(user?.role || '').toLowerCase() === 'task_only';
  const adminPermissionOptions = {
    role: user?.role,
    permissions: user?.permissions,
    isExternalAccount: user?.is_external_account === true,
  };
  const canUseAdminPersonalChat =
    user?.userType === 'admin' &&
    !isTaskOnlyAdmin &&
    resolveAdminPermission({
      permissionKey: 'NAV_PERSONAL_CHAT',
      ...adminPermissionOptions,
    });
  const canUseAdminGroupChat =
    user?.userType === 'admin' &&
    !isTaskOnlyAdmin &&
    resolveAdminPermission({
      permissionKey: 'NAV_GROUP_CHAT',
      ...adminPermissionOptions,
    });
  const canUseAdminEventChat =
    user?.userType === 'admin' &&
    !isTaskOnlyAdmin &&
    resolveAdminPermission({
      permissionKey: 'NAV_EVENT_CHAT',
      ...adminPermissionOptions,
    });
  const canFetchAdminEventLabel =
    user?.userType === 'admin' &&
    !isTaskOnlyAdmin &&
    (
      resolveAdminPermission({
        permissionKey: 'EVENT_VIEW_PUBLISHED_DETAIL',
        ...adminPermissionOptions,
      }) ||
      resolveAdminPermission({
        permissionKey: 'EVENT_VIEW_UNPUBLISHED_DETAIL',
        ...adminPermissionOptions,
      }) ||
      resolveAdminPermission({
        permissionKey: 'NAV_EVENTS',
        ...adminPermissionOptions,
      })
    );

  const eventLabelCacheRef = useRef(new Map());

  const currentPath = () =>
    typeof window !== 'undefined' ? window.location.pathname : '';

  const pathRole = (() => {
    const path = currentPath();
    if (path.startsWith('/admin')) return 'admin';
    if (path.startsWith('/artist')) return 'artist';
    return null;
  })();

  const role = isTaskOnlyAdmin
    ? null
    : pathRole ||
      (user?.userType === 'admin'
        ? 'admin'
        : artistUser?.userType === 'artist'
          ? 'artist'
          : null);

  const dispatchPeerNotification = (detail) => {
    try {
      window.dispatchEvent(new CustomEvent('peer:notification', { detail }));
    } catch (_) {
      // noop
    }
  };

  const isSamePersonalChatOpen = (payload = {}) =>
    isMatchingActiveChatContext(getActiveChatContext(), {
      scope: role,
      channel: 'personal',
      artistId: payload.artistId || payload.artist_id || '',
    });

  const isSameEventChatOpen = (payload = {}) =>
    isMatchingActiveChatContext(getActiveChatContext(), {
      scope: role,
      channel: 'event',
      eventId:
        payload.eventId ||
        payload.event_id ||
        payload.eventUuid ||
        payload.event_uuid ||
        '',
      threadId: payload.threadId || payload.thread_id || '',
    });

  const isSameGroupChatOpen = (payload = {}) =>
    isMatchingActiveChatContext(getActiveChatContext(), {
      scope: role,
      channel: 'group',
      threadId: payload.threadId || payload.thread_id || '',
    });

  const buildEventLabel = (payload = {}) => {
    const nameRaw = payload.eventName || payload.event_name || payload.name;
    const perfRaw = payload.performanceType || payload.performance_type;
    const name = typeof nameRaw === 'string' ? nameRaw.trim() : '';
    const performance = typeof perfRaw === 'string' ? perfRaw.trim() : '';
    return [name, performance].filter(Boolean).join(' ');
  };

  const fetchEventLabel = async (eventId) => {
    if (!eventId) return '';
    if (role === 'admin' && !canFetchAdminEventLabel) return '';
    const cached = eventLabelCacheRef.current.get(eventId);
    if (cached) return cached;
    try {
      const event = await getEventDetail(eventId);
      const label = buildEventLabel(event || {});
      if (label) {
        eventLabelCacheRef.current.set(eventId, label);
      }
      return label;
    } catch (_) {
      return '';
    }
  };

  /* --------------------------------------------------
   * 受信イベントハンドラ
   * -------------------------------------------------- */
  const handleNewMessage = (payload = {}) => {
    if (!role) return;
    if (role === 'admin' && !canUseAdminPersonalChat) return;
    const messageFormat = resolveMessageFormat(payload);
    if (isSystemMessageFormat(messageFormat)) return;
    const senderArtistId = resolveSenderArtistId(payload);
    if (role === 'admin') {
      const isNormalArtistMessage =
        senderArtistId != null && (!messageFormat || NORMAL_CHAT_FORMATS.has(messageFormat));
      if (!isNormalArtistMessage) return;
    }
    const messageId = payload.messageId || payload.message_id || '';
    const dedupKey = messageId
      ? `personal:${messageId}`
      : `personal:${payload.artistId || payload.artist_id || ''}:${payload.adminUserId || payload.admin_user_id || ''}:${
          messageFormat || ''
        }`;
    const now = Date.now();
    if (!GlobalSocketListener._personalSeen || !(GlobalSocketListener._personalSeen instanceof Map)) {
      GlobalSocketListener._personalSeen = new Map();
    }
    const seen = GlobalSocketListener._personalSeen;
    const lastTs = seen.get(dedupKey);
    if (lastTs && now - lastTs < 3000) {
      return;
    }
    seen.set(dedupKey, now);
    const MAX_SEEN = 500;
    if (seen.size > MAX_SEEN) {
      const entries = Array.from(seen.entries()).sort((a, b) => a[1] - b[1]);
      for (let i = 0; i < entries.length - MAX_SEEN; i += 1) {
        seen.delete(entries[i][0]);
      }
    }

    const isSelfAdminSender =
      role === 'admin' &&
      payload.adminUserId != null &&
      String(payload.adminUserId) === String(user?.userId);
    const isSelfArtistSender =
      role === 'artist' &&
      senderArtistId != null &&
      String(senderArtistId) === String(artistUser?.userId);

    if (!isSamePersonalChatOpen(payload) && !isSelfAdminSender && !isSelfArtistSender) {
      const summary =
        payload.adminUserId && role === 'artist'
          ? 'タクティカートからチャットが届きました'
          : payload.artistId && role === 'admin'
            ? 'アーティストからチャットが届きました'
            : '新着チャットがあります';
      const targetUrl =
        role === 'artist'
          ? '/artist/workspaces/communications/chat'
          : '/admin/workspaces/communications/artist-chat';
      dispatchPeerNotification({
        id: messageId ? `personal:${messageId}` : undefined,
        type: 'chat.message',
        title: 'チャットの新着',
        summary,
        targetUrl,
        audience: role,
        icon: 'chat',
        color: 'var(--green-500)',
      });
    }
  };

  const handleNewEventMessage = async (payload = {}) => {
    if (!role) return;
    if (role === 'admin' && !canUseAdminEventChat) return;
    const messageFormat = resolveMessageFormat(payload);
    if (isSystemMessageFormat(messageFormat)) return;
    const messageId = payload.messageId || payload.message_id || '';
    const senderType = payload.senderType || payload.sender_type || '';
    const senderId = payload.senderId || payload.sender_id || null;
    const adminUserId = user?.userId ?? user?.id ?? null;
    const artistUserId = artistUser?.userId ?? artistUser?.id ?? null;
    const isSelfAdminEventSender =
      role === 'admin' &&
      senderType === 'admin' &&
      senderId != null &&
      adminUserId != null &&
      String(senderId) === String(adminUserId);
    const isSelfArtistEventSender =
      role === 'artist' &&
      senderType === 'artist' &&
      senderId != null &&
      artistUserId != null &&
      String(senderId) === String(artistUserId);
    if (isSelfAdminEventSender || isSelfArtistEventSender) {
      return;
    }
    if (role === 'admin') {
      const isNormalArtistMessage =
        senderType === 'artist' && (!messageFormat || NORMAL_CHAT_FORMATS.has(messageFormat));
      if (!isNormalArtistMessage) return;
    }
    const dedupKey = messageId
      ? `event:${messageId}`
      : `event:${payload.eventId || ''}:${payload.senderType || payload.sender_type || ''}:${
          payload.senderId || payload.sender_id || ''
        }:${messageFormat}`;
    const now = Date.now();
    if (!GlobalSocketListener._eventSeen || !(GlobalSocketListener._eventSeen instanceof Map)) {
      GlobalSocketListener._eventSeen = new Map();
    }
    const seen = GlobalSocketListener._eventSeen;
    const lastTs = seen.get(dedupKey);
    if (lastTs && now - lastTs < 3000) {
      return;
    }
    seen.set(dedupKey, now);
    const MAX_SEEN = 500;
    if (seen.size > MAX_SEEN) {
      const entries = Array.from(seen.entries()).sort((a, b) => a[1] - b[1]);
      for (let i = 0; i < entries.length - MAX_SEEN; i += 1) {
        seen.delete(entries[i][0]);
      }
    }

    if (!isSameEventChatOpen(payload)) {
      const targetUrl =
        role === 'artist'
          ? '/artist/workspaces/event-operations/event-chat'
          : '/admin/workspaces/event-operations/event-chat';
      const directLabel = buildEventLabel(payload);
      const resolvedLabel = directLabel || await fetchEventLabel(payload?.eventId);
      const summary = resolvedLabel || 'イベントチャットに新着があります';
      dispatchPeerNotification({
        id: messageId ? `event:${messageId}` : undefined,
        type: 'chat.message',
        title: 'イベントチャットの新着',
        summary,
        targetUrl,
        audience: role,
        icon: 'event',
        color: 'var(--green-500)',
      });
    }
  };

  const handleRealtimeNotification = (payload = {}) => {
    if (!payload || !payload.id) return;
    const audience = payload.audience || 'all';
    if (audience !== 'all' && audience !== role) return;
    if (role === 'artist' && Array.isArray(payload.artistIds) && payload.artistIds.length) {
      if (!payload.artistIds.map(String).includes(String(artistUser?.userId))) return;
    }
    if (role === 'admin' && Array.isArray(payload.adminIds) && payload.adminIds.length) {
      if (!payload.adminIds.map(String).includes(String(user?.userId))) return;
    }
    // 重複排除（短時間のみ抑止。一定時間を超えたら再度表示可）
    const DEDUP_MS = 3000; // 3秒以内の同一IDは重複とみなす
    if (!GlobalSocketListener._seen || !(GlobalSocketListener._seen instanceof Map)) {
      GlobalSocketListener._seen = new Map();
    }
    const seen = GlobalSocketListener._seen;
    const now = Date.now();
    const lastTs = seen.get(payload.id);
    if (lastTs && now - lastTs < DEDUP_MS) {
      return;
    }
    seen.set(payload.id, now);
    const MAX_SEEN = 500;
    if (seen.size > MAX_SEEN) {
      // 古い順に間引きする
      const entries = Array.from(seen.entries()).sort((a, b) => a[1] - b[1]);
      for (let i = 0; i < entries.length - MAX_SEEN; i += 1) {
        seen.delete(entries[i][0]);
      }
    }
    dispatchPeerNotification(payload);
  };

  const handleNewGroupChatMessage = (payload = {}) => {
    if (!role) return;
    if (role === 'admin' && !canUseAdminGroupChat) return;
    const messageFormat = resolveMessageFormat(payload);
    if (isSystemMessageFormat(messageFormat)) return;
    const messageId = payload.messageId || payload.message_id || '';
    const senderType = String(payload.senderType || payload.sender_type || '').toLowerCase();
    const senderId = payload.senderId || payload.sender_id || null;
    const adminUserId = user?.userId ?? user?.id ?? null;
    const artistUserId = artistUser?.userId ?? artistUser?.id ?? null;
    const isSelfAdminSender =
      role === 'admin' &&
      senderType === 'admin' &&
      senderId != null &&
      adminUserId != null &&
      String(senderId) === String(adminUserId);
    const isSelfArtistSender =
      role === 'artist' &&
      senderType === 'artist' &&
      senderId != null &&
      artistUserId != null &&
      String(senderId) === String(artistUserId);
    if (isSelfAdminSender || isSelfArtistSender) {
      return;
    }

    const dedupKey = messageId
      ? `group:${messageId}`
      : `group:${payload.threadId || payload.thread_id || ''}:${senderType}:${senderId || ''}:${
          messageFormat || ''
        }`;
    const now = Date.now();
    if (!GlobalSocketListener._groupSeen || !(GlobalSocketListener._groupSeen instanceof Map)) {
      GlobalSocketListener._groupSeen = new Map();
    }
    const seen = GlobalSocketListener._groupSeen;
    const lastTs = seen.get(dedupKey);
    if (lastTs && now - lastTs < 3000) {
      return;
    }
    seen.set(dedupKey, now);
    const MAX_SEEN = 500;
    if (seen.size > MAX_SEEN) {
      const entries = Array.from(seen.entries()).sort((a, b) => a[1] - b[1]);
      for (let i = 0; i < entries.length - MAX_SEEN; i += 1) {
        seen.delete(entries[i][0]);
      }
    }

    if (isSameGroupChatOpen(payload)) return;

    const threadTitleRaw = payload.threadTitle || payload.thread_title || payload.title;
    const threadTitle = typeof threadTitleRaw === 'string' ? threadTitleRaw.trim() : '';
    const targetUrl = role === 'artist' ? '/artist/workspaces/communications/chat' : '/admin/workspaces/communications/group-chat';
    dispatchPeerNotification({
      id: messageId ? `group-message:${messageId}` : undefined,
      type: 'chat.message',
      title: 'グループチャットの新着',
      summary: threadTitle || 'グループチャットに新着があります',
      targetUrl,
      audience: role,
      icon: 'chat',
      color: 'var(--green-500)',
    });
  };

  const handleContractBadgeChanged = () => {
    dispatchAdminContractBadgeRefresh({ role });
  };

  /* -------------------------------------------------- */
  // ソケット購読は role / user 切替時に再バインドし、各 handler はその時点のクロージャを使う
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!role) return; // 未ログイン時は何もしない

    const socket = getSocket();
    if (!socket.connected) {
      try { socket.connect(); } catch (e) { console.error('[socket] connect error:', e); }
    }

    /* ソケットイベント登録 */
    socket.on('new_message',    handleNewMessage);
    socket.on(SOCKET_EVENTS.NEW_EVENT_MESSAGE, handleNewEventMessage);
    socket.on(SOCKET_EVENTS.NEW_GROUP_CHAT_MESSAGE, handleNewGroupChatMessage);
    socket.on(SOCKET_EVENTS.REALTIME_NOTIFICATION, handleRealtimeNotification);
    socket.on(SOCKET_EVENTS.CONTRACT_BADGE_CHANGED, handleContractBadgeChanged);
    socket.on('connect_error', (e) => console.warn('[socket] connect_error', e?.message || e));
    socket.on('reconnect_attempt', (n) => console.info('[socket] reconnect_attempt', n));
    socket.on('reconnect_failed', () => console.warn('[socket] reconnect_failed'));

    /* クリーンアップ */
    return () => {
      socket.off('new_message',   handleNewMessage);
      socket.off(SOCKET_EVENTS.NEW_EVENT_MESSAGE, handleNewEventMessage);
      socket.off(SOCKET_EVENTS.NEW_GROUP_CHAT_MESSAGE, handleNewGroupChatMessage);
      socket.off(SOCKET_EVENTS.REALTIME_NOTIFICATION, handleRealtimeNotification);
      socket.off(SOCKET_EVENTS.CONTRACT_BADGE_CHANGED, handleContractBadgeChanged);
      socket.off('connect_error');
      socket.off('reconnect_attempt');
      socket.off('reconnect_failed');
    };
    // user / artistUser が変わったら再バインド
  }, [artistUser, user, role]);

  return null; // 画面には何も描画しない
};

export default GlobalSocketListener;
